import { useSearch } from "@redotech/react-util/search";
import { useTranslation } from "@redotech/redo-i18n";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import type { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import Fuse from "fuse.js";
import { memo, useMemo, useState, type ReactNode } from "react";

import * as styles from "./action-editor.module.css";

export interface ActionDefinition {
  option: RedoListItem<string>;
  selected: boolean;
  onSelect(): void;
  inlineFields?: ReactNode;
  fields?: ReactNode;
  joinText?: string;
}

interface ActionEditorProps {
  actions: ActionDefinition[];
  onClose?: () => void;
}

export const ActionEditor = memo(function ActionEditor({
  actions,
  onClose,
}: ActionEditorProps) {
  const { t } = useTranslation();
  const [searchString, setSearchString] = useState("");
  const searcher = useMemo(
    () =>
      new Fuse<RedoListItem<string>>([], {
        keys: ["text"],
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [],
  );
  const options = useMemo(
    () => actions.map((action) => action.option),
    [actions],
  );
  const filteredOptions = useSearch(searcher, options, searchString);
  const selectedAction = actions.find((action) => action.selected);

  function selectAction({ item }: { item: RedoListItem<string> }) {
    const action = actions.find((candidate) => candidate.option.id === item.id);
    if (!action) {
      throw new Error("Unknown action");
    }
    action.onSelect();
  }

  return (
    <Flex dir="column" justify="flex-start">
      <Flex justify="space-between" py="sm" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          {t("Action")}
        </Text>
        {onClose && (
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        )}
      </Flex>
      <Flex align="center" dir="row" gap="md" overflow="visible" wrap="wrap">
        <div className={styles.actionSelect}>
          <RedoSingleSelectDropdownInput<string>
            fitToAnchor={false}
            flexGrow={false}
            options={filteredOptions}
            optionSelected={selectAction}
            placeholder={t("Select an action")}
            searchPlaceholder={t("Search actions...")}
            searchString={searchString}
            selectedItem={selectedAction?.option}
            setSearchString={setSearchString}
            size="xs"
          />
        </div>
        {selectedAction?.joinText && (
          <Text fontSize="xs" fontWeight="medium">
            {selectedAction.joinText}
          </Text>
        )}
        {selectedAction?.inlineFields}
      </Flex>
      {selectedAction?.fields}
    </Flex>
  );
});
